import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ContextMenuItem } from "@shared/components/context-menu/context-menu";

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.css',
  standalone: false
})

/**
 * A reusable dropdown menu component for selecting from a list of options.
 *
 * @param dropdownOptions          List of ContextMenuItem objects to display as options.
 * @param defaultSelectedOption    (Optional) The option to be selected by default.
 * @param optionClicked            Emits the selected ContextMenuItem when an option is clicked.
 *
 * @property selectedOption        The currently selected option.
 * @property isOpen                Boolean indicating if the dropdown is open.
 *
 * @description
 * - Displays the selected option and a caret icon.
 * - Opens a menu of options on click.
 * - Supports optional badges and descriptions per option.
 * - Emits an event when a selection is made.
 * - Caret icon updates for open/close state.
 */

export class DropdownComponent implements OnInit, OnChanges {
  @Input() dropdownOptions: ContextMenuItem[] = [];
  @Input() defaultSelectedOption?: string | null = null;
  @Output() optionClicked = new EventEmitter<ContextMenuItem>();

  selectedOption: ContextMenuItem | null = null;
  isOpen: boolean = false;

  ngOnInit(): void {
    this.setDefaultSelectedOption();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['defaultSelectedOption'] || changes['dropdownOptions']) {
      this.setDefaultSelectedOption();
    }
  }

  toggle() {
    if (!this.dropdownOptions?.length) return;
    this.isOpen = !this.isOpen;
  }

  select(option: ContextMenuItem) {
    this.selectedOption = option;
    this.toggle();
    this.optionClicked.emit(option);
  }

  get caretIcon() {
    return this.isOpen ?
      '../../../../assets/icons/caret/caretUp-11px-mono-black.svg'
      : '../../../../assets/icons/caret/caretDown-11px-mono-black.svg';
  }

  private setDefaultSelectedOption() {
    if (this.defaultSelectedOption) {
      const defaultOption = this.dropdownOptions
        .find(option => option.id === this.defaultSelectedOption);
      if (defaultOption) {
        this.selectedOption = defaultOption;
        return;
      }
    }
  }
}
